import React, { useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { FormItem, FormLabel, FormControl, FormMessage } from "../ui/form";
import { Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { CreatableCombobox } from "../CreatableCombobox";
import { InlineLoader } from "../Loader";
import {
  useGetGpuDetailQuery,
  useCreateGpuMutation,
  useUpdateGpuMutation,
  useGetGpuTypeQuery,
  useGetGpuInstanceListQuery,
} from "../../store/api/gpuApi";
import {
  extractGpuCardFromInstance,
  generateGpuName,
  calculateAvailableGpu,
  formatGpuDataForApi,
} from "../../lib/gpuUtils";

const formSchema = z.object({
  instance_type: z.string().min(1, "Instance type is required"),
  gpu_card: z.string().min(1, "GPU card is required"),
  gpuname: z.string().min(1, "GPU name is required"),
  gpu_type: z.string().min(1, "GPU type is required"),
  gpu_committed: z.number().min(0, "Must be a non-negative number").optional(),
  gpu_installed: z.number().min(0, "Must be a non-negative number").optional(),
  gpu_provided: z.number().min(0, "Must be a non-negative number").optional(),
  gpu_allocated: z.number().min(0, "Must be a non-negative number").optional(),
  gpu_utilised: z.number().min(0, "Must be a non-negative number").optional(),
  gpu_available: z.number().min(0, "Available GPUs cannot be negative"),
});

const GpuFormModal = ({ isOpen, onClose, id = null, mode = "create" }) => {
  const isEditMode = mode === "edit";
  const [selectedInstanceType, setSelectedInstanceType] = useState("");
  const [openDropdown, setOpenDropdown] = useState(false);

  // Fetch GPU instance list from API
  const { data: gpuInstanceList = [] } = useGetGpuInstanceListQuery(undefined, {
    skip: !isOpen,
  });

  const {
    data: gpuData,
    isLoading: isLoadingGpu,
    error: fetchError,
  } = useGetGpuDetailQuery(id, {
    skip: !isOpen || !isEditMode,
  });

  const { data: gpuTypeData, isFetching: isLoadingGpuType } =
    useGetGpuTypeQuery(selectedInstanceType, {
      skip: !selectedInstanceType || !isOpen,
    });

  const [createGpu, { isLoading: isCreating }] = useCreateGpuMutation();
  const [updateGpu, { isLoading: isUpdating }] = useUpdateGpuMutation();

  const isSubmitting = isCreating || isUpdating;

  const {
    register,
    handleSubmit,
    watch,
    reset,
    control,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      instance_type: "",
      gpu_card: "",
      gpuname: "",
      gpu_type: "",
      gpu_committed: 0,
      gpu_installed: 0,
      gpu_provided: 0,
      gpu_allocated: 0,
      gpu_utilised: 0,
      gpu_available: 0,
    },
  });

  const gpuInstalledValue = watch("gpu_installed");
  const gpuAllocatedValue = watch("gpu_allocated");

  // Handle instance_type selection with auto-derivation
  const handleInstanceTypeSelect = (value) => {
    setSelectedInstanceType(value);
    setValue("instance_type", value);

    // Auto-derive gpu_card from instance type (e.g., "H100PCIe.8x" → "8")
    const extractedCard = extractGpuCardFromInstance(value);
    setValue("gpu_card", extractedCard);

    // Auto-generate gpuname
    const generatedName = generateGpuName(value);
    setValue("gpuname", generatedName);
  };

  // Update gpu_available when gpu_installed or gpu_allocated changes
  useEffect(() => {
    const available = calculateAvailableGpu(
      gpuInstalledValue,
      gpuAllocatedValue,
    );
    setValue("gpu_available", available);
  }, [gpuInstalledValue, gpuAllocatedValue, setValue]);

  // Update gpu_type when API responds
  useEffect(() => {
    if (gpuTypeData) {
      setValue("gpu_type", gpuTypeData);
    }
  }, [gpuTypeData, setValue]);

  // Load existing data for edit mode
  useEffect(() => {
    if (isEditMode && gpuData) {
      reset({
        instance_type: gpuData.instance_type,
        gpu_card: gpuData.gpu_card,
        gpuname: gpuData.gpuname,
        gpu_type: gpuData.gpu_type,
        gpu_committed: gpuData.gpu_committed,
        gpu_installed: gpuData.gpu_installed,
        gpu_provided: gpuData.gpu_provided,
        gpu_allocated: gpuData.gpu_allocated,
        gpu_utilised: gpuData.gpu_utilised,
        gpu_available: gpuData.gpu_available,
      });
      setSelectedInstanceType(gpuData.instance_type);
    } else if (!isEditMode && isOpen) {
      reset({
        instance_type: "",
        gpu_card: "",
        gpuname: "",
        gpu_type: "",
        gpu_committed: 0,
        gpu_installed: 0,
        gpu_provided: 0,
        gpu_allocated: 0,
        gpu_utilised: 0,
        gpu_available: 0,
      });
      setSelectedInstanceType("");
    }
  }, [gpuData, reset, isEditMode, isOpen]);

  const onSubmit = async (values) => {
    try {
      const formattedData = formatGpuDataForApi(values);

      if (isEditMode) {
        await updateGpu({
          id,
          ...formattedData,
        }).unwrap();
      } else {
        await createGpu(formattedData).unwrap();
      }
      onClose();
    } catch (error) {
      console.error(
        `Failed to ${isEditMode ? "update" : "create"} GPU:`,
        error,
      );
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto pt-4">
        <DialogHeader>
          <DialogTitle>{isEditMode ? "Edit" : "Create"} GPU</DialogTitle>
        </DialogHeader>

        {isEditMode && isLoadingGpu ? (
          <div className="flex items-center justify-center py-8">
            <InlineLoader label="Loading GPU data..." />
          </div>
        ) : isEditMode && fetchError ? (
          <div className="text-center py-8 text-destructive">
            Error loading GPU data. Please try again.
          </div>
        ) : (
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            {/* Instance Type - Searchable Dropdown */}
            <FormItem>
              <FormLabel>Instance Type *</FormLabel>
              <FormControl>
                <div>
                  <Controller
                    name="instance_type"
                    control={control}
                    render={({ field }) => (
                      <CreatableCombobox
                        items={gpuInstanceList}
                        value={field.value}
                        onSelect={(val) => {
                          handleInstanceTypeSelect(val);
                          setOpenDropdown(false);
                        }}
                        placeholder="Select or type instance type..."
                      />
                    )}
                  />
                  {errors.instance_type && (
                    <FormMessage>{errors.instance_type.message}</FormMessage>
                  )}
                </div>
              </FormControl>
            </FormItem>

            {/* GPU Card - Auto-derived, Read-only */}
            <FormItem>
              <FormLabel>GPU Card (Auto-derived)</FormLabel>
              <FormControl>
                <Input
                  type="text"
                  {...register("gpu_card")}
                  disabled={isSubmitting}
                  readOnly
                  placeholder="Auto-filled from instance type"
                />
              </FormControl>
              {errors.gpu_card && (
                <FormMessage>{errors.gpu_card.message}</FormMessage>
              )}
            </FormItem>

            {/* GPU Name - Auto-generated, User can edit */}
            <FormItem>
              <FormLabel>GPU Name *</FormLabel>
              <FormControl>
                <Input
                  type="text"
                  {...register("gpuname")}
                  disabled={isSubmitting}
                  placeholder="Auto-generated, editable"
                />
              </FormControl>
              {errors.gpuname && (
                <FormMessage>{errors.gpuname.message}</FormMessage>
              )}
            </FormItem>

            {/* GPU Type - Auto-fetched, Read-only */}
            <FormItem>
              <FormLabel>
                GPU Type{" "}
                {isLoadingGpuType && (
                  <span className="text-xs text-gray-400">(fetching...)</span>
                )}
              </FormLabel>
              <FormControl>
                <Input
                  type="text"
                  {...register("gpu_type")}
                  disabled={isSubmitting}
                  readOnly
                  placeholder="Auto-fetched from API"
                />
              </FormControl>
              {errors.gpu_type && (
                <FormMessage>{errors.gpu_type.message}</FormMessage>
              )}
            </FormItem>

            {/* GPU Committed */}
            <FormItem>
              <FormLabel>GPU Committed</FormLabel>
              <FormControl>
                <Input
                  type="number"
                  {...register("gpu_committed", { valueAsNumber: true })}
                  disabled={isSubmitting}
                  min="0"
                />
              </FormControl>
              {errors.gpu_committed && (
                <FormMessage>{errors.gpu_committed.message}</FormMessage>
              )}
            </FormItem>

            {/* GPU Installed */}
            <FormItem>
              <FormLabel>GPU Installed</FormLabel>
              <FormControl>
                <Input
                  type="number"
                  {...register("gpu_installed", { valueAsNumber: true })}
                  disabled={isSubmitting}
                  min="0"
                />
              </FormControl>
              {errors.gpu_installed && (
                <FormMessage>{errors.gpu_installed.message}</FormMessage>
              )}
            </FormItem>

            {/* GPU Provided */}
            <FormItem>
              <FormLabel>GPU Provided</FormLabel>
              <FormControl>
                <Input
                  type="number"
                  {...register("gpu_provided", { valueAsNumber: true })}
                  disabled={isSubmitting}
                  min="0"
                />
              </FormControl>
              {errors.gpu_provided && (
                <FormMessage>{errors.gpu_provided.message}</FormMessage>
              )}
            </FormItem>

            {/* GPU Allocated */}
            <FormItem>
              <FormLabel>GPU Allocated</FormLabel>
              <FormControl>
                <Input
                  type="number"
                  {...register("gpu_allocated", { valueAsNumber: true })}
                  disabled={isSubmitting}
                  min="0"
                />
              </FormControl>
              {errors.gpu_allocated && (
                <FormMessage>{errors.gpu_allocated.message}</FormMessage>
              )}
            </FormItem>

            {/* GPU Utilised */}
            <FormItem>
              <FormLabel>GPU Utilised</FormLabel>
              <FormControl>
                <Input
                  type="number"
                  {...register("gpu_utilised", { valueAsNumber: true })}
                  disabled={isSubmitting}
                  min="0"
                />
              </FormControl>
              {errors.gpu_utilised && (
                <FormMessage>{errors.gpu_utilised.message}</FormMessage>
              )}
            </FormItem>

            {/* GPU Available - User editable */}
            <FormItem>
              <FormLabel>GPU Available</FormLabel>
              <FormControl>
                <Input
                  type="number"
                  {...register("gpu_available", { valueAsNumber: true })}
                  disabled={isSubmitting}
                  min="0"
                  placeholder="Manually input or will auto-calculate"
                />
              </FormControl>
              {errors.gpu_available && (
                <FormMessage>{errors.gpu_available.message}</FormMessage>
              )}
            </FormItem>

            <DialogFooter>
              <div className="flex justify-end space-x-2">
                <Button
                  variant="outline"
                  type="button"
                  onClick={onClose}
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={isSubmitting}>
                  {isSubmitting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      {isEditMode ? "Saving..." : "Creating..."}
                    </>
                  ) : isEditMode ? (
                    "Save Changes"
                  ) : (
                    "Create"
                  )}
                </Button>
              </div>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default GpuFormModal;
